/**
 * /waitlist — join the Clerk waitlist.
 *
 * The marketing site's "Join the waitlist" CTA (apps/www) links here.
 * Requires the Clerk instance to run in Waitlist sign-up mode (Clerk
 * Dashboard → Restrictions → Sign-up mode: Waitlist); the Frontend API
 * rejects `joinWaitlist` otherwise.
 *
 * Flow:
 *   clerk.joinWaitlist({ emailAddress })
 *
 * On success we show a confirmation screen rather than redirecting, so
 * the visitor gets clear acknowledgement they're on the list.
 */

import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { joinWaitlist } from '@/lib/auth';
import { useClerk } from '@clerk/react';
import { CheckCircle } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_auth/waitlist')({
  component: WaitlistPage,
});

function WaitlistPage(): ReactElement {
  const clerk = useClerk();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setSubmitting(true);
    setEmailError(undefined);
    setFormError(undefined);

    const result = await joinWaitlist({ clerk, emailAddress: email });

    if (result.kind === 'error') {
      setEmailError(result.fieldErrors.emailAddress);
      setFormError(result.fieldErrors.emailAddress ? undefined : result.formError);
      setSubmitting(false);
      return;
    }

    setJoined(true);
    setSubmitting(false);
  }

  if (joined) {
    return (
      <div className="space-y-4 text-center">
        <div className="flex justify-center text-[var(--color-success)]">
          <CheckCircle aria-hidden="true" size={40} weight="regular" />
        </div>
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">You're on the list</h1>
        <p className="text-sm text-[var(--color-mute)]">
          We added <span className="font-medium text-[var(--color-ink)]">{email}</span> to the
          waitlist. We'll email you when your spot opens up.
        </p>
        <p className="text-xs text-[var(--color-mute)]">
          Wrong email?{' '}
          <button
            type="button"
            className="font-medium text-[var(--color-accent-brand)] hover:underline"
            onClick={() => setJoined(false)}
          >
            Join with a different one
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Join the waitlist</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Clickweave is in early access. Leave your email and we'll let you know when you're in.
        </p>
      </header>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            aria-invalid={emailError ? true : undefined}
          />
          {emailError && <FieldError>{emailError}</FieldError>}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        {/* Clerk requires a CAPTCHA element to be present for bot
            protection on waitlist joins. Hidden unless Clerk uses it. */}
        <div id="clerk-captcha" />

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Joining…' : 'Join the waitlist'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
